import React, {FC, useEffect, useRef, useState} from "react";
import {
    Accordion,
    AccordionDetails, AccordionSummary, AccordionSummaryProps,
    Avatar,
    Box, Divider, Grid,
    Typography
} from "@mui/material";
import {GradientButton, GradientText} from "./GradientComponents";
import {UserChatsHistory} from "../providers/useModel";
import {Message as IMessage} from "../providers/useModel";
import MarkdownContent from "./MarkdownContent";
import MediumSquare from "../icons/MediumSquare";
import "./chart.css";
import {ExpandMore as ExpandMoreIcon} from "@mui/icons-material";
import {styled} from "@mui/material/styles";
import Instruction from "./Instruction";

// const useStyles = makeStyles((theme: Theme) =>
//     createStyles({
//         messageRow: {
//             display: "flex"
//         },
//         messageRowRight: {
//             display: "flex",
//             justifyContent: "flex-end"
//         },
//         messageBot: {
//             display: 'flex',
//             position: "relative",
//             textAlign: "left",
//             padding: 12,
//             borderRadius: "0px 12px 12px 12px",
//             border: '1px solid #E2E8F0',
//             gap: 8,
//             background: 'linear-gradient(0deg, #FFFFFF, #FFFFFF), linear-gradient(0deg, #E2E8F0, #E2E8F0)',
//             boxShadow: '0px 1px 2px 0px #0F1A2A14'
//         },
//         messageUser: {
//             display: 'flex',
//             position: "relative",
//             textAlign: "left",
//             padding: 12,
//             borderRadius: "12px 12px 0px 12px",
//             border: '1px solid #E2E8F0',
//             gap: 8,
//             background: 'linear-gradient(0deg, #FFFFFF, #FFFFFF), linear-gradient(0deg, #E2E8F0, #E2E8F0)',
//             boxShadow: '0px 1px 2px 0px #0F1A2A14'
//         },
//
//         messageTimeStampRight: {
//             position: "absolute",
//             fontSize: ".7em",
//             fontWeight: "300",
//             marginTop: "10px",
//             right: "10px",
//             bottom: "2px"
//         },
//
//         orange: {
//             color: "black", //theme.palette.getContrastText(deepOrange[500]),
//             backgroundColor: deepOrange[500],
//             width: 4,
//             height: 4
//         },
//         avatarNothing: {
//             color: "transparent",
//             backgroundColor: "transparent",
//             width: 4,
//             height: 4
//         },
//         displayName: {
//             marginLeft: "20px"
//         }
//     })
// );

interface ChatProps {
    messages?: IMessage[];
    messageSubmitted?: (message: string) => void;
    placeholderText?: string;
    minRows?: number;
    userDisplayName?: string;
    userPhotoUrl?: string;
    botDisplayName?: string;
    botPhotoUrl?: string;
    isGenerating?: boolean;
    stopGenerating?: () => void;
    height?: string;
    userChatHistory : UserChatsHistory;
}

interface MessageProps extends IMessage {
    timestamp?: string;
    photoURL?: string;
    displayName?: string;
    showTimestamp?: boolean;
    showAvatar?: boolean;
    style?: React.CSSProperties;
    isGenerating?: boolean;
    stopGenerating?: () => void;
}

export const Message: FC<MessageProps> = ({
                                              text,
                                              modifiedText,
                                              timestamp,
                                              photoURL,
                                              displayName,
                                              role,
                                              showTimestamp = false,
                                              showAvatar = false,
                                              style = {},
                                              isGenerating,
                                              stopGenerating = () => {
                                              }
                                          }) => {
    // const classes = useStyles();
    return (
        <Box display={"flex"}
             justifyContent={role === 'human' ? "flex-end" : "flex-start"}
             style={{marginBottom: 12, ...style}}
        >
            <Box display={"inline-flex"} flexDirection={"column"}
                 style={{maxWidth: '90%'}}>
                <Grid container direction={"column"}
                      flexWrap={"nowrap"}
                      className={role === 'human' ? 'messageUser' : 'messageBot'}>
                    <Grid item>
                        <Grid container flexWrap={"nowrap"}>
                            {role === 'human' && (
                                <Grid item justifyContent={"center"}
                                      sx={{mt: 0.5}}>
                                    <GradientText>{text}</GradientText>
                                    {
                                        modifiedText && modifiedText !== text && (
                                            <Typography variant={"body2"}>
                                            {modifiedText}
                                        </Typography>)
                                    }
                                </Grid>)
                            }
                            {showAvatar && (<Grid item
                                                  display={"flex"}
                                                  alignItems={role === 'human' ? "flex-end" : "flex-start"}
                                                  sx={{
                                                      ml: role === 'human' ? 2 : 0,
                                                      mr: role === 'human' ? 0 : 2,
                                                      mt: 0.5,
                                                  }}>
                                <Avatar
                                    variant={"circular"}
                                    alt={displayName}
                                    src={photoURL}
                                    sx={{
                                        height: 28,
                                        width: 28
                                    }}
                                ></Avatar>
                            </Grid>)
                            }
                            {role === 'assistant' && (
                                <Grid item justifyContent={"center"}>
                                    <MarkdownContent text={text}
                                                     isGenerating={isGenerating}
                                                     noPadding
                                    />
                                </Grid>)
                            }
                        </Grid>
                    </Grid>
                    {
                        isGenerating && role === 'assistant' && (
                            <Grid item xs={6} style={{
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                display: 'flex'
                            }}>
                                <GradientButton variant={"text"}
                                                startIcon={<MediumSquare gradient/>}
                                                style={{
                                                    textTransform: 'none',
                                                    minWidth: 150,
                                                    paddingLeft: 0
                                                }}
                                                onClick={() => stopGenerating()}
                                >
                                    <GradientText
                                        variant={"body2"}>
                                        Stop Generating
                                    </GradientText>
                                </GradientButton>
                            </Grid>
                        )
                    }
                </Grid>

                {
                    showTimestamp && (
                        <Typography className={"messageTimeStampRight"}>{timestamp}</Typography>)
                }
            </Box>
        </Box>
    )
        ;
};

const CustomAccordionSummary = styled((props: AccordionSummaryProps) => (
    <AccordionSummary
        expandIcon={<ExpandMoreIcon/>}
        {...props}
    />))(({ theme }) => ({
    '& .MuiAccordionSummary-content': {
        maxWidth: '100%',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1)
    },
}))

const Chat: FC<ChatProps> = ({
                                 messageSubmitted = () => {
                                 },
                                 placeholderText, minRows,
                                 userPhotoUrl, userDisplayName,
                                 isGenerating,
                                 height,
                                 userChatHistory,
                                 stopGenerating = () => {
                                 }
                             }) => {
    const [instruction, setInstruction] = useState<string>("");
    const chatBoxRef = useRef<HTMLDivElement>(null);
    const [messageCount, setMessageCount] = useState<number>(0);
    const systemPromptChatRef = React.useRef<HTMLDivElement>(null);

    // useEffect(() => {
    //     if (chatBoxRef.current) {
    //         const initialChats: IMessage[] = []
    //         const messages: IMessage[] = userChatHistory.userChats.reduce((allChats , {chats}) => ([...allChats, ...chats]), initialChats);
    //
    //         const isScrolledToBottom =
    //             Math.abs(chatBoxRef.current.scrollTop + chatBoxRef.current.clientHeight - chatBoxRef.current.scrollHeight) < 100;
    //
    //         if (isScrolledToBottom) {
    //             setTimeout(() => {
    //                 if (chatBoxRef.current) {
    //                     chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    //                 }
    //             }, 0);
    //         } else if (messageCount < messages.length) {
    //             if (chatBoxRef.current) {
    //                 chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    //                 setMessageCount(messages.length)
    //             }
    //         }
    //     }
    // }, [messages]);

    // const {
    //     isGenerating,
    // } = useModel();
    return (
        <Box display={"flex"} flexDirection={"column"} justifyContent={"space-between"} style={{height: '100%', backgroundColor: "#FAF9FE"}}>
            <Box display={"flex"} flexDirection={"column"}
                 ref={chatBoxRef}
                 style={{
                     overflow: 'auto',
                     overflowAnchor: 'auto',
                     padding: 24
                 }}
            >
                {
                    userChatHistory.userChats.map(({systemPrompt, chats}, index) => {
                        return (<div key={index}>
                            {
                                (userChatHistory.userChats.length !== 1 ) && <div key={index}>
                                    <Accordion
                                        ref={systemPromptChatRef}
                                        style={{
                                            boxShadow: '0px 0px 0px 0px #0F1A2A14',
                                            width: '100%',
                                            padding: 0,
                                            backgroundColor: "transparent",
                                            marginBottom: "12px",
                                            border: "none"
                                        }}>
                                        <Grid container style={{display: "flex",
                                            alignItems: "center"}}>
                                            <Grid item xs={4.2}>
                                                <Divider variant="fullWidth"/>
                                            </Grid>
                                            <Grid item xs={3.6}>
                                                <CustomAccordionSummary>
                                                    <Typography
                                                        sx={{
                                                            color: 'text.secondary',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}>System prompt Edited</Typography>
                                                </CustomAccordionSummary>
                                            </Grid>
                                            <Grid item xs={4.2}>
                                                <Divider variant="fullWidth"/>
                                            </Grid>
                                        </Grid>
                                        <AccordionDetails style={{padding: 0, margin: "0 14px 4px 14px"}}>
                                            <Instruction instruction={systemPrompt}
                                                         minRows={3}
                                                         disableIcon={true}
                                                         disabled={false}
                                                         setInstruction={() => {}}
                                                         instructionSubmitted={() => {}}
                                                         placeholderText={""}
                                                         isGenerating={false} />
                                        </AccordionDetails>
                                    </Accordion>
                                </div>
                            }
                            {
                                chats.map((message, userChatIndex) => {
                                    if (message.role === 'human') {
                                        return (
                                            <Message
                                                key={`${index}-${userChatIndex}`}
                                                role={"human"}
                                                showAvatar={true}
                                                displayName={userDisplayName}
                                                photoURL={userPhotoUrl}
                                                text={message.text}
                                                modifiedText={message.modifiedText}
                                            />
                                        );
                                    } else if (message.role === 'assistant') {
                                        return (
                                            <Message
                                                key={`${index}-${userChatIndex}`}
                                                role={"assistant"}
                                                showAvatar={true}
                                                displayName={"Symbl"}
                                                photoURL={`/nebula-avatar.png`}
                                                text={message.text}
                                                isGenerating={isGenerating && (index === userChatHistory.userChats.length - 1) && (userChatIndex === chats.length - 1)}
                                                stopGenerating={stopGenerating}
                                            />
                                        );
                                    }
                                })
                            }
                        </div>)

                    })
                }
                {/*{*/}
                {/*    messages.map((message, index) => {*/}
                {/*        if (message.role === 'human') {*/}
                {/*            return (*/}
                {/*                <Message*/}
                {/*                    key={index}*/}
                {/*                    role={"human"}*/}
                {/*                    showAvatar={true}*/}
                {/*                    displayName={userDisplayName}*/}
                {/*                    photoURL={userPhotoUrl}*/}
                {/*                    text={message.text}*/}
                {/*                    modifiedText={message.modifiedText}*/}
                {/*                />*/}
                {/*            );*/}
                {/*        } else if (message.role === 'assistant') {*/}
                {/*            return (*/}
                {/*                <Message*/}
                {/*                    key={index}*/}
                {/*                    role={"assistant"}*/}
                {/*                    showAvatar={true}*/}
                {/*                    displayName={"Symbl"}*/}
                {/*                    photoURL={`${window.location.pathname}/nebula-avatar.png`}*/}
                {/*                    text={message.text}*/}
                {/*                    isGenerating={isGenerating && index >= messages.length - 1}*/}
                {/*                    stopGenerating={stopGenerating}*/}
                {/*                />*/}
                {/*            );*/}
                {/*        }*/}
                {/*    })*/}
                {/*}*/}

                {/*<div style={{*/}
                {/*    overflowAnchor: 'auto',*/}
                {/*    height: '1px'*/}
                {/*}}/>*/}
            </Box>
            <Box display="flex"
                 flexDirection="column"
                 style={{marginBottom: 16, marginLeft: 16, marginRight: 16}}>
                <Instruction
                    placeholderText={"Send a message"}
                    instruction={instruction}
                    instructionSubmitted={(message) => {
                        setInstruction("");
                        messageSubmitted(message);
                    }}
                    setInstruction={setInstruction}
                    isGenerating={!!isGenerating}
                />
            </Box>
        </Box>
    );
};

export default Chat;
